import { Fieldset, Paragraph, Radio, Textfield, ValidationMessage } from '@digdir/designsystemet-react';
import { Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { SolcellerFormData } from '../../types';
import { useSolcelleFormContext } from '../utils';
import { useSolcellerValidation } from '../useSolcellerValidation';
import { parseNorwegianFormattedNumber } from '../../../../../../utils/utils';

export const ProductionData = () => {
  const { t } = useTranslation();
  const { register, watch, formState, getValues, trigger } = useSolcelleFormContext();
  const { validateKwhWithSpecificYieldCap } = useSolcellerValidation();

  const productionDataSelection = watch('productionData');
  const sharedSolarInstallation = watch('sharedSolarInstallation');
  const sharedSolarDistribution = watch('sharedSolarDistribution');
  const totalSharedBoenheter = watch('totalSharedBoenheter');
  const fordelingsnokkelPercent = watch('fordelingsnokkelPercent');
  const productionDataScope = watch('productionDataScope');
  const hasCapacityData = watch('hasCapacityData');
  const installationYear = watch('installationYear');
  const kwpPanels = watch('kwpPanels');
  const sqmPanels = watch('sqmPanels');

  useEffect(() => {
    if (productionDataSelection !== 'actualProduction' && productionDataSelection !== 'calculatedProduction') {
      return;
    }
    void trigger(['producedKwh', 'calculatedKwh']);
  }, [
    productionDataSelection,
    sharedSolarInstallation,
    sharedSolarDistribution,
    totalSharedBoenheter,
    fordelingsnokkelPercent,
    productionDataScope,
    hasCapacityData,
    installationYear,
    kwpPanels,
    sqmPanels,
    trigger
  ]);

  const getFormValues = () => getValues() as SolcellerFormData;
  const shouldAskProductionDataScope =
    sharedSolarInstallation === 'yes' &&
    (productionDataSelection === 'actualProduction' || productionDataSelection === 'calculatedProduction');
  const productionDataScopeLabel =
    productionDataScope === 'thisBoenhet'
      ? t('register.solar.form.productionData.scope.inputLabel.thisBoenhet')
      : t('register.solar.form.productionData.scope.inputLabel.wholeInstallation');

  // Renders radio with corresponding input based on selection
  return (
    <Fragment>
      <Fieldset>
        <Fieldset.Legend>{t('register.solar.form.productionData.label')}</Fieldset.Legend>
        <Radio
          {...register('productionData', { required: t('register.solar.form.productionData.error') })}
          label={t('register.solar.form.productionData.option.average')}
          value="actualProduction"
        />
        <Radio
          {...register('productionData', { required: t('register.solar.form.productionData.error') })}
          label={t('register.solar.form.productionData.option.calculated')}
          value="calculatedProduction"
        />
        <Radio
          {...register('productionData', { required: t('register.solar.form.productionData.error') })}
          label={t('register.solar.form.productionData.option.unknown')}
          value="unknown"
        />
        {formState.errors.productionData && (
          <ValidationMessage data-color="danger">{formState.errors.productionData.message}</ValidationMessage>
        )}
      </Fieldset>

      {shouldAskProductionDataScope && (
        <Fieldset>
          <Fieldset.Legend>{t('register.solar.form.productionData.scope.label')}</Fieldset.Legend>
          <Radio
            {...register('productionDataScope', {
              required: t('register.solar.form.productionData.scope.error')
            })}
            label={t('register.solar.form.productionData.scope.option.wholeInstallation')}
            value="wholeInstallation"
          />
          <Radio
            {...register('productionDataScope', {
              required: t('register.solar.form.productionData.scope.error')
            })}
            label={t('register.solar.form.productionData.scope.option.thisBoenhet')}
            value="thisBoenhet"
          />
          {formState.errors.productionDataScope && (
            <ValidationMessage data-color="danger">
              {formState.errors.productionDataScope.message}
            </ValidationMessage>
          )}
        </Fieldset>
      )}

      {productionDataSelection === 'actualProduction' && (
        <Textfield
          type="text"
          inputMode="decimal"
          {...register('producedKwh', {
            setValueAs: (value) => parseNorwegianFormattedNumber(value),
            required: t('register.solar.form.productionData.faktiskProdusert.error'),
            validate: (value) => validateKwhWithSpecificYieldCap(value, getFormValues)
          })}
          error={formState.errors.producedKwh?.message}
          label={
            shouldAskProductionDataScope
              ? t('register.solar.form.productionData.faktiskProdusert.labelWithScope', {
                scope: productionDataScopeLabel
              })
              : t('register.solar.form.productionData.faktiskProdusert.label')
          }
          suffix={t('register.solar.form.suffix.kwhYear')}
        />
      )}
      {productionDataSelection === 'calculatedProduction' && (
        <Textfield
          type="text"
          inputMode="decimal"
          {...register('calculatedKwh', {
            setValueAs: (value) => parseNorwegianFormattedNumber(value),
            required: t('register.solar.form.productionData.beregnetProduksjon.error'),
            validate: (value) => validateKwhWithSpecificYieldCap(value, getFormValues)
          })}
          error={formState.errors.calculatedKwh?.message}
          label={
            shouldAskProductionDataScope
              ? t('register.solar.form.productionData.beregnetProduksjon.labelWithScope', {
                scope: productionDataScopeLabel
              })
              : t('register.solar.form.productionData.beregnetProduksjon.label')
          }
          suffix={t('register.solar.form.suffix.kwhYear')}
        />
      )}
      {productionDataSelection === 'unknown' && (
        <Paragraph>{t('register.solar.form.productionData.unknown.label')}</Paragraph>
      )}
    </Fragment>
  );
};
